import { isAxiosError } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useFeatureFlag } from '~/features/feature-flags/useFeatureFlag';
import { CALENDAR_API } from '~/features/provider-hlx/calendar/api';
import type { AvailabilityDay, AvailabilityRule } from '~/features/provider-hlx/calendar/types';
import { toISODate } from '~/features/shared/calendar/dateUtils';
import { extractBackendErrorDetail } from '~/features/utils';
import { buildCalendarUrl } from '~/routing';
import type { Provider } from '~/types/mcv/provider';
import { getData, patchData, postData } from '~/utils/service';
import { buildAvailabilitySummaryRanges, isAvailabilityReady } from './availabilityReadiness';
import type {
  AoiSpecialtyApi,
  FormState,
  ProviderServiceListingApi,
} from './providerServiceListingForm';
import {
  buildPayload,
  INITIAL_FORM_STATE,
  mapApiListing,
  mapApiListingToFormState,
  validateExpectedRates,
  validateRadius,
  validateSpecialtySelections,
} from './providerServiceListingForm';
import { ProviderServiceListingsView } from './ProviderServiceListingsView';
import { publicationErrorMessage } from './publicationFeedback';
import type { AvailabilityMode, PublicationFeedback, ServiceListing } from './types';

/** Manage service listings and resume the selected listing after calendar edits. */
export const ProviderServiceListings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedListingId = Number(searchParams.get('serviceListingId'));
  const calendarEnabled = useFeatureFlag('providerCalendar');
  const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE);
  const [listings, setListings] = useState<ServiceListing[]>([]);
  const [licenseStateOptions, setLicenseStateOptions] = useState<string[]>([]);
  const [specialtyOptions, setSpecialtyOptions] = useState<AoiSpecialtyApi[]>([]);
  const [specialtyOptionsError, setSpecialtyOptionsError] = useState<string | null>(null);
  const [providerCategoryId, setProviderCategoryId] = useState<number | null>(null);
  const [selectedListingId, setSelectedListingId] = useState<number | null>(null);
  const [availabilityMode, setAvailabilityMode] = useState<AvailabilityMode>('GENERAL');
  const [calendarDays, setCalendarDays] = useState<AvailabilityDay[]>([]);
  const [recurringRules, setRecurringRules] = useState<AvailabilityRule[]>([]);
  const [isCalendarLoading, setIsCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [publishingListingIds, setPublishingListingIds] = useState<Set<number>>(() => new Set());
  const [publicationFeedbackByListing, setPublicationFeedbackByListing] = useState<
    Map<number, PublicationFeedback>
  >(() => new Map());
  const publicationOperations = useRef(new Map<number, object>());

  const clearPublicationFeedback = (listingId: number) => {
    publicationOperations.current.delete(listingId);
    setPublicationFeedbackByListing((current) => {
      if (!current.has(listingId)) return current;
      const feedback = new Map(current);
      feedback.delete(listingId);
      return feedback;
    });
  };

  useEffect(() => {
    const operations = publicationOperations.current;
    return () => operations.clear();
  }, []);
  const [isLoadingEditor, setIsLoadingEditor] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [editingListingId, setEditingListingId] = useState<number | null>(null);
  const [editingListingSource, setEditingListingSource] =
    useState<ProviderServiceListingApi | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const loadListings = async () => {
      try {
        const provider = await getData<Provider>('currentProvider');
        const resolvedProviderCategoryId =
          provider.category?.id ?? provider.type?.providerCategoryId;
        let specialtyRequestFailed = false;
        const [suggestedStates, apiListings, canonicalSpecialties] = await Promise.all([
          getData<string[]>('providerServiceLicenseStateSuggestions'),
          getData<ProviderServiceListingApi[]>('providerServiceListings'),
          resolvedProviderCategoryId
            ? getData<AoiSpecialtyApi[]>('aoisSpec', {
                pathParams: { providerCategoryId: String(resolvedProviderCategoryId) },
              }).catch(() => {
                specialtyRequestFailed = true;
                return [];
              })
            : Promise.resolve([]),
        ]);
        if (!isMounted) return;

        setProviderCategoryId(resolvedProviderCategoryId ?? null);
        setSpecialtyOptions(canonicalSpecialties);
        setSpecialtyOptionsError(
          specialtyRequestFailed
            ? 'Specialty options are unavailable. Existing canonical selections can be preserved, but new specialties cannot be saved.'
            : null
        );
        const canonicalSpecialtyIds = Object.fromEntries(
          canonicalSpecialties.map((specialty) => [specialty.aoiSpecialtyName, specialty.id])
        );
        setFormState((current) => ({
          ...current,
          aoiSpecialties: current.aoiSpecialties.filter(
            (name) => canonicalSpecialtyIds[name] || current.specialtyIdsByName[name]
          ),
          specialtyIdsByName: {
            ...canonicalSpecialtyIds,
            ...current.specialtyIdsByName,
          },
        }));

        // [MCV-1864] Only the states the provider is currently licensed in can be chosen.
        setLicenseStateOptions([...new Set(suggestedStates)]);

        const mappedListings = apiListings.map(mapApiListing);
        const requestedListing = mappedListings.find(
          (listing) => listing.id === requestedListingId
        );
        setListings(mappedListings);
        if (requestedListing) setAvailabilityMode('LISTING');
        setSelectedListingId(
          (current) => requestedListing?.id ?? current ?? mappedListings[0]?.id ?? null
        );
      } catch {
        if (isMounted) {
          setStatusMessage('Unable to load provider service listings.');
        }
      }
    };

    loadListings();
    return () => {
      isMounted = false;
    };
  }, [requestedListingId]);

  useEffect(() => {
    if (!calendarEnabled) {
      setCalendarDays([]);
      setRecurringRules([]);
      setCalendarError(null);
      setIsCalendarLoading(false);
      return;
    }

    let isMounted = true;
    const today = new Date();
    setIsCalendarLoading(true);
    setCalendarError(null);

    Promise.all([
      Promise.all(
        buildAvailabilitySummaryRanges(today).map(({ start, end }) =>
          CALENDAR_API.getRange({ start, end, scope: { kind: 'all' } })
        )
      ),
      CALENDAR_API.getRules(),
    ])
      .then(([ranges, rules]) => {
        if (!isMounted) return;
        setCalendarDays(ranges.flatMap((range) => range.days));
        setRecurringRules(rules);
      })
      .catch(() => {
        if (isMounted) {
          setCalendarError('Unable to load availability. Open Calendar to try again.');
        }
      })
      .finally(() => {
        if (isMounted) setIsCalendarLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [calendarEnabled]);

  const selectedListing = useMemo(
    () => listings.find((listing) => listing.id === selectedListingId),
    [listings, selectedListingId]
  );

  const availabilityReady = useMemo(() => {
    if (!calendarEnabled && formState.kind !== 'FULL_TIME') return false;
    return isAvailabilityReady({
      calendarDays,
      kind: formState.kind,
      preferredStartDate: formState.preferredStartDate,
      recurringRules,
      selectedListingId: editingListingId,
      todayISO: toISODate(new Date()),
    });
  }, [
    calendarDays,
    calendarEnabled,
    editingListingId,
    formState.kind,
    formState.preferredStartDate,
    recurringRules,
  ]);

  const expectedRateErrors = useMemo(() => validateExpectedRates(formState), [formState]);
  const radiusError = useMemo(() => validateRadius(formState), [formState]);
  const specialtySelectionError = useMemo(
    () => validateSpecialtySelections(formState),
    [formState]
  );

  /** Save the form before optionally opening Calendar for the persisted listing ID. */
  const handleSaveDraft = async (openCalendar = false) => {
    if (editingListingId != null) clearPublicationFeedback(editingListingId);
    if (!providerCategoryId) {
      setFormError('Provider category is required before saving.');
      return;
    }
    if (!formState.preferredStartDate) {
      setFormError('Preferred work start date is required before saving.');
      return;
    }
    if (Object.keys(expectedRateErrors).length > 0) {
      setFormError('Correct the highlighted expected-rate fields before saving.');
      return;
    }
    if (radiusError) {
      setFormError('Correct the highlighted radius before saving.');
      return;
    }
    if (specialtySelectionError) {
      setFormError(specialtySelectionError);
      return;
    }
    setIsSaving(true);
    setFormError(null);
    setStatusMessage(null);
    try {
      const wasEditing = editingListingId != null;
      const payload = buildPayload(formState, providerCategoryId, editingListingSource);
      const saved = editingListingId
        ? await patchData<ProviderServiceListingApi>('providerServiceListings', {
            data: payload,
            pathParams: { listingId: String(editingListingId) },
          })
        : await postData<ProviderServiceListingApi>('providerServiceListings', {
            data: payload,
          });
      const mapped = mapApiListing(saved);
      setListings((current) => {
        const existingIndex = current.findIndex((listing) => listing.id === mapped.id);
        if (existingIndex === -1) return [mapped, ...current];
        return current.map((listing) => (listing.id === mapped.id ? mapped : listing));
      });
      setSelectedListingId(mapped.id);
      setIsCreateDialogOpen(false);
      setEditingListingId(null);
      setEditingListingSource(null);
      setStatusMessage(
        saved.publishValidationErrors?.length
          ? `Listing updated and paused: ${saved.publishValidationErrors.join(' ')}`
          : wasEditing
            ? 'Listing updated.'
            : 'Draft saved.'
      );
      if (openCalendar) navigate(buildCalendarUrl({ serviceListingId: mapped.id }));
    } catch (error: unknown) {
      // [MCV-1864] A 400 explains itself ("You are not currently licensed in XX."); show it.
      const detail = isAxiosError(error)
        ? error.response?.status === 400
          ? extractBackendErrorDetail(error.response.data?.detail)
          : undefined
        : undefined;
      const base = editingListingId ? 'Unable to update listing.' : 'Unable to save draft.';
      setFormError(`${base} ${detail ?? 'Please try again.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNew = () => {
    const canonicalSpecialtyIds = Object.fromEntries(
      specialtyOptions.map((specialty) => [specialty.aoiSpecialtyName, specialty.id])
    );
    setEditingListingId(null);
    setEditingListingSource(null);
    // [MCV-1864] INITIAL_FORM_STATE holds no license states: a new listing starts with none picked.
    setFormState({
      ...INITIAL_FORM_STATE,
      aoiSpecialties: INITIAL_FORM_STATE.aoiSpecialties.filter(
        (specialty) => canonicalSpecialtyIds[specialty]
      ),
      specialtyIdsByName: canonicalSpecialtyIds,
    });
    setFormError(null);
    setStatusMessage(null);
    setIsCreateDialogOpen(true);
  };

  const handleEdit = async () => {
    if (selectedListingId == null || publishingListingIds.has(selectedListingId)) return;
    clearPublicationFeedback(selectedListingId);
    setIsLoadingEditor(true);
    setFormError(null);
    setStatusMessage(null);
    try {
      const listing = await getData<ProviderServiceListingApi>('providerServiceListing', {
        pathParams: { listingId: String(selectedListingId) },
      });
      const canonicalSpecialtyIds = Object.fromEntries(
        specialtyOptions.map((specialty) => [specialty.aoiSpecialtyName, specialty.id])
      );
      const mapped = mapApiListingToFormState(listing);
      setFormState({
        ...mapped,
        specialtyIdsByName: {
          ...canonicalSpecialtyIds,
          ...mapped.specialtyIdsByName,
        },
      });
      setEditingListingSource(listing);
      setEditingListingId(listing.id);
      setIsCreateDialogOpen(true);
    } catch {
      setStatusMessage('Unable to load this listing for editing.');
    } finally {
      setIsLoadingEditor(false);
    }
  };

  const handleArchive = async () => {
    if (selectedListingId == null) return;
    const listingId = selectedListingId;
    setIsArchiving(true);
    setArchiveError(null);
    setStatusMessage(null);
    try {
      await patchData<ProviderServiceListingApi>('providerServiceListingArchive', {
        pathParams: { listingId: String(listingId) },
      });
      clearPublicationFeedback(listingId);
      const remaining = listings.filter((listing) => listing.id !== listingId);
      setListings((current) => current.filter((listing) => listing.id !== listingId));
      setSelectedListingId((current) =>
        current === listingId ? (remaining[0]?.id ?? null) : current
      );
      setIsArchiveDialogOpen(false);
      setStatusMessage('Listing archived.');
    } catch {
      // The dialog stays open so the user can retry, and it is portaled above
      // the page-level status region -- so the failure has to render inside it.
      setArchiveError('Unable to archive this listing. Please try again.');
    } finally {
      setIsArchiving(false);
    }
  };

  const handlePublish = async () => {
    if (selectedListingId == null || publishingListingIds.has(selectedListingId)) return;
    const listingId = selectedListingId;
    const operation = { listingId };
    clearPublicationFeedback(listingId);
    publicationOperations.current.set(listingId, operation);
    setPublishingListingIds((current) => new Set(current).add(listingId));
    try {
      const published = await patchData<ProviderServiceListingApi>(
        'providerServiceListingActivate',
        { pathParams: { listingId: String(listingId) } }
      );
      const mapped = mapApiListing(published);
      setListings((current) =>
        current.map((listing) => (listing.id === mapped.id ? mapped : listing))
      );
      if (publicationOperations.current.get(listingId) === operation) {
        setPublicationFeedbackByListing((current) =>
          new Map(current).set(listingId, {
            listingId,
            outcome: 'success',
            message: 'Listing published.',
          })
        );
      }
    } catch (error: unknown) {
      if (publicationOperations.current.get(listingId) === operation) {
        setPublicationFeedbackByListing((current) =>
          new Map(current).set(listingId, {
            listingId,
            outcome: 'error',
            message: publicationErrorMessage(error),
          })
        );
      }
    } finally {
      if (publicationOperations.current.get(listingId) === operation) {
        publicationOperations.current.delete(listingId);
      }
      setPublishingListingIds((current) => {
        const pending = new Set(current);
        pending.delete(listingId);
        return pending;
      });
    }
  };

  const handleCloseCreateDialog = () => {
    if (isSaving) return;
    setFormError(null);
    setIsCreateDialogOpen(false);
    setEditingListingId(null);
    setEditingListingSource(null);
  };

  return (
    <ProviderServiceListingsView
      availabilityMode={availabilityMode}
      availabilityReady={availabilityReady}
      calendarDays={calendarDays}
      calendarEnabled={calendarEnabled}
      calendarError={calendarError}
      editingListingId={editingListingId}
      expectedRateErrors={expectedRateErrors}
      formError={formError}
      formState={formState}
      isCreateDialogOpen={isCreateDialogOpen}
      isCalendarLoading={isCalendarLoading}
      archiveError={archiveError}
      isArchiveDialogOpen={isArchiveDialogOpen}
      isArchiving={isArchiving}
      isLoadingEditor={isLoadingEditor}
      isPublishing={selectedListingId != null && publishingListingIds.has(selectedListingId)}
      isSaving={isSaving}
      heldLicenseStates={editingListingSource?.licenseStates?.map((state) => state.stateCode) ?? []}
      licenseStateOptions={licenseStateOptions}
      listings={listings}
      onAvailabilityModeChange={setAvailabilityMode}
      onArchive={handleArchive}
      onCancelArchive={() => {
        setArchiveError(null);
        setIsArchiveDialogOpen(false);
      }}
      onCloseCreateDialog={handleCloseCreateDialog}
      onEdit={handleEdit}
      onFormStateChange={setFormState}
      onNew={handleNew}
      onPublish={handlePublish}
      onSaveDraft={handleSaveDraft}
      onSaveAndOpenCalendar={() => handleSaveDraft(true)}
      onSelectListing={setSelectedListingId}
      onShowArchive={() => {
        setArchiveError(null);
        setIsArchiveDialogOpen(true);
      }}
      publicationFeedback={
        selectedListingId != null && !isCreateDialogOpen && !isLoadingEditor && !isArchiveDialogOpen
          ? publicationFeedbackByListing.get(selectedListingId)
          : null
      }
      radiusError={radiusError}
      recurringRules={recurringRules}
      selectedListing={selectedListing}
      selectedListingId={selectedListingId}
      specialtyOptions={specialtyOptions.map((specialty) => specialty.aoiSpecialtyName)}
      specialtyOptionsError={specialtyOptionsError}
      statusMessage={statusMessage}
    />
  );
};
